import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
} from '@mui/material';

import { useUpdateSupplier } from '~/hooks/apis/tours/useTourSuppliers';

import { Supplier } from '~/types/tours/supplier';

const MIN_PERCENT = 1;
const MAX_PERCENT = 99;

interface Props {
  open: boolean;
  supplier: Supplier;
  onClose: () => void;
}

function profitSharePercent(profitShareRate: number | null): string {
  return profitShareRate === null ? '' : String(Math.round(profitShareRate * 100));
}

export default function SupplierProfitShareDialog({ open, supplier, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const updateSupplier = useUpdateSupplier(supplier.id);

  const [percent, setPercent] = useState(profitSharePercent(supplier.profitShareRate));

  const value = percent.trim();
  const parsed = Number(value);
  const isValid = !value || (Number.isInteger(parsed) && parsed >= MIN_PERCENT && parsed <= MAX_PERCENT);

  const handleSave = useCallback(async () => {
    try {
      await updateSupplier.mutateAsync({ profitShareRate: value ? parsed / 100 : null });
      enqueueSnackbar(`Saved the profit share for ${supplier.name}`, { variant: 'success' });
      onClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  }, [enqueueSnackbar, onClose, parsed, supplier.name, updateSupplier, value]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Profit share</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <TextField
            label="Profit share"
            type="number"
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            error={!isValid}
            helperText={
              isValid
                ? 'The share of what it is paid that the supplier keeps. Leave blank for none.'
                : `A whole percentage from ${MIN_PERCENT} to ${MAX_PERCENT}`
            }
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            autoFocus
          />

          {!!updateSupplier.error && <Alert severity="error">{updateSupplier.error.message}</Alert>}
        </Stack>
      </DialogContent>
      {updateSupplier.isPending && <LinearProgress />}
      <DialogActions>
        <Button onClick={onClose} disabled={updateSupplier.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid || updateSupplier.isPending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
